import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@/core/api";
import type {
  ISheetApprovalRequestResponse,
  ISheetApprovalRequestState,
} from "./types";

const SHEET_APPROVAL_REQUESTS_KEY = "sheet-approval-requests";

export const sheetApprovalRequestsKey = (
  businessId: string | null,
  runId: string | null,
) => [SHEET_APPROVAL_REQUESTS_KEY, businessId, runId] as const;

const getSheetApprovalRequestsPath = (businessId: string, runId: string) =>
  `/businesses/${businessId}/ai_script_runs/${runId}/sheet_approval_requests`;

/**
 * Who would be asked to approve this run's sheets, and what was already asked.
 * Drives both whether the button appears and the confirmation copy — a manager
 * should never fire off emails without seeing the recipient list first.
 */
export const useGetSheetApprovalRequestState = (
  businessId: string | null,
  runId: string | null,
  enabled = true,
) => {
  const api = useApi();

  return useQuery({
    queryKey: sheetApprovalRequestsKey(businessId, runId),
    queryFn: () => {
      if (!businessId || !runId) throw new Error("Payroll run is required");

      return api
        .get(getSheetApprovalRequestsPath(businessId, runId))
        .then((res) => res.data as ISheetApprovalRequestState);
    },
    enabled: enabled && !!businessId && !!runId,
    retry: false,
  });
};

export const useRequestSheetApprovals = (businessId: string | null) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ runId, userIds }: { runId: string; userIds: string[] }) => {
      if (!businessId) throw new Error("Business is required");

      return api
        .post(getSheetApprovalRequestsPath(businessId, runId), {
          user_ids: userIds,
        })
        .then((res) => res.data as ISheetApprovalRequestResponse);
    },
    onSuccess: (_response, { runId }) => {
      // Refetch so the header can show when the last ask went out, and so a
      // failed delivery shows up rather than reading as sent.
      void queryClient.invalidateQueries({
        queryKey: sheetApprovalRequestsKey(businessId, runId),
      });
    },
  });
};
